import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { crmApi, type CrmLeadProject, type CrmLeadProjectInput, type LeadProjectStatus } from '@/services/api';
import { useOrganizationStore } from '@/stores/organization';
import { PERMISSIONS } from '@/types';
import { cardClass, inputClass, labelClass } from './styles';

const STATUS_LABEL: Record<LeadProjectStatus, string> = {
  abierto: 'Abierto',
  en_propuesta: 'En propuesta',
  aceptado: 'Aceptado',
  descartado: 'Descartado',
};
const STATUS_COLOR: Record<LeadProjectStatus, string> = {
  abierto: 'bg-gray-100 text-gray-700',
  en_propuesta: 'bg-blue-100 text-blue-700',
  aceptado: 'bg-green-100 text-green-700',
  descartado: 'bg-red-50 text-red-700',
};
// Sugerencias para el checklist ABC; se puede escribir cualquier otra.
const SOLUTION_SUGGESTIONS = ['LIVRE SYSTEM', 'Web destino (PET-VIDA)', 'Catálogo', 'Tienda online', 'Landing Asociación', 'Plan', 'Presencia', 'Notificaciones', 'Analítica', 'Planificador'];
const ABC: { key: 'essential' | 'recommended' | 'useful'; label: string }[] = [
  { key: 'essential', label: 'A · Imprescindibles' },
  { key: 'recommended', label: 'B · Recomendables' },
  { key: 'useful', label: 'C · Útiles' },
];

const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

/** Proyectos del lead (ALTA LEAD 2.1): uno o varios por ficha. */
export default function LeadProjectsSection({ contactId }: { contactId: string }) {
  const canManage = useOrganizationStore((s) => s.hasOrgPermission(PERMISSIONS.CRM_MANAGE));
  const [list, setList] = useState<CrmLeadProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CrmLeadProject | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try { setList((await crmApi.listProjects(contactId)).data.projects || []); }
    catch { /* sin permiso: silencioso */ } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [contactId]);

  const remove = async (p: CrmLeadProject) => {
    if (!confirm(`¿Eliminar el proyecto «${p.name}»?`)) return;
    try { await crmApi.removeProject(p._id); toast.success('Proyecto eliminado'); void load(); }
    catch (e) { toast.error(errMsg(e, 'No se pudo eliminar')); }
  };

  return (
    <div className="mt-5 border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Proyectos ({list.length})</h3>
        {canManage && (
          <button onClick={() => setEditing('new')} className="inline-flex items-center gap-1 text-sm font-medium text-vida-700 hover:bg-vida-50 px-2 py-1 rounded">
            <PlusIcon className="w-4 h-4" /> Nuevo proyecto
          </button>
        )}
      </div>
      {loading ? (
        <p className="text-xs text-gray-400 mt-2">Cargando…</p>
      ) : list.length === 0 ? (
        <p className="text-xs text-gray-400 mt-2">Aún no hay proyectos para este lead.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {list.map((p) => (
            <li key={p._id} className="flex items-center gap-2 text-sm rounded px-2 py-1.5 hover:bg-gray-50">
              <button onClick={() => setEditing(p)} className="flex-1 min-w-0 text-left truncate text-gray-800 hover:text-vida-700">{p.name}</button>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLOR[p.status]}`}>{STATUS_LABEL[p.status]}</span>
              {canManage && (
                <button onClick={() => void remove(p)} aria-label={`Eliminar ${p.name}`} className="p-1 text-gray-300 hover:text-red-600 flex-shrink-0">
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <ProjectEditor contactId={contactId} project={editing === 'new' ? null : editing} readOnly={!canManage}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} />
      )}
    </div>
  );
}

function SolutionsInput({ value, onChange, disabled }: { value: string[]; onChange: (v: string[]) => void; disabled: boolean }) {
  const [draft, setDraft] = useState('');
  const add = (s: string) => {
    const v = s.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-1">
        {value.map((s) => (
          <button key={s} type="button" disabled={disabled} onClick={() => onChange(value.filter((x) => x !== s))}
            className="px-2 py-0.5 rounded-full text-xs bg-vida-100 text-vida-800 hover:bg-vida-200 disabled:hover:bg-vida-100">
            {s}{!disabled && ' ×'}
          </button>
        ))}
      </div>
      {!disabled && (
        <input className={inputClass} list="lead-solution-suggestions" placeholder="Escribe una solución y pulsa Intro" value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(draft); } }}
          onBlur={() => draft && add(draft)} />
      )}
    </div>
  );
}

function ProjectEditor({ contactId, project, readOnly, onClose, onSaved }: {
  contactId: string; project: CrmLeadProject | null; readOnly: boolean; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<Required<Pick<CrmLeadProjectInput, 'name' | 'status'>> & Pick<CrmLeadProject, 'client' | 'internal'>>({
    name: project?.name || '',
    status: project?.status || 'abierto',
    client: project?.client || {},
    internal: project?.internal || {},
  });
  const [busy, setBusy] = useState(false);
  const client = form.client;
  const internal = form.internal;
  const objectives = client.objectives || {};
  const legal = client.legalUrls || {};
  const solutions = internal.solutions || {};
  const setClient = (patch: Partial<CrmLeadProject['client']>) => setForm({ ...form, client: { ...client, ...patch } });
  const setInternal = (patch: Partial<CrmLeadProject['internal']>) => setForm({ ...form, internal: { ...internal, ...patch } });

  const submit = async () => {
    if (!form.name.trim()) { toast.error('El proyecto necesita un nombre'); return; }
    setBusy(true);
    // La fecha de aceptación la sella el servidor; no se envía.
    const clientInput = { ...client };
    delete clientInput.vidaTermsAcceptedAt;
    const data: CrmLeadProjectInput = { name: form.name, status: form.status, client: clientInput, internal };
    try {
      if (project) await crmApi.updateProject(project._id, data);
      else await crmApi.createProject(contactId, data);
      toast.success(project ? 'Proyecto guardado' : 'Proyecto creado');
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar')); } finally { setBusy(false); }
  };

  const ta = (value: string | undefined, onChange: (v: string) => void, rows = 2) => (
    <textarea className={inputClass} rows={rows} disabled={readOnly} value={value || ''} onChange={(e) => onChange(e.target.value)} />
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
      <div className={`${cardClass} w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto`} role="dialog" aria-modal="true" aria-label="Proyecto del lead">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">{project ? 'Proyecto' : 'Nuevo proyecto'}</h2>
        <datalist id="lead-solution-suggestions">{SOLUTION_SUGGESTIONS.map((s) => <option key={s} value={s} />)}</datalist>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2"><label className={labelClass}>Nombre del proyecto</label>
            <input className={inputClass} disabled={readOnly} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><label className={labelClass}>Estado</label>
            <select className={inputClass} disabled={readOnly} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as LeadProjectStatus })}>
              {(Object.keys(STATUS_LABEL) as LeadProjectStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select></div>
        </div>

        <h3 className="text-sm font-semibold text-gray-900 mt-5">Lo que quiere el cliente</h3>
        <p className="text-xs text-gray-400 mb-2">El lead puede ver y rellenar esta parte.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><label className={labelClass}>Descripción</label>{ta(client.description, (v) => setClient({ description: v }), 3)}</div>
          <div><label className={labelClass}>Objetivos corporativos</label>{ta(objectives.corporate, (v) => setClient({ objectives: { ...objectives, corporate: v } }))}</div>
          <div><label className={labelClass}>Objetivos sociales</label>{ta(objectives.social, (v) => setClient({ objectives: { ...objectives, social: v } }))}</div>
          <div><label className={labelClass}>Objetivos económicos</label>{ta(objectives.economic, (v) => setClient({ objectives: { ...objectives, economic: v } }))}</div>
          <div><label className={labelClass}>Objetivos medioambientales</label>{ta(objectives.environmental, (v) => setClient({ objectives: { ...objectives, environmental: v } }))}</div>
          <div><label className={labelClass}>CNAE</label>
            <input className={inputClass} disabled={readOnly} value={client.cnae || ''} onChange={(e) => setClient({ cnae: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
            <input type="checkbox" disabled={readOnly} checked={!!client.activityRegistered} onChange={(e) => setClient({ activityRegistered: e.target.checked })} />
            Actividad dada de alta
          </label>
          <div><label className={labelClass}>URL aviso legal</label>
            <input className={inputClass} disabled={readOnly} placeholder="https://" value={legal.legalNotice || ''} onChange={(e) => setClient({ legalUrls: { ...legal, legalNotice: e.target.value } })} /></div>
          <div><label className={labelClass}>URL privacidad</label>
            <input className={inputClass} disabled={readOnly} placeholder="https://" value={legal.privacy || ''} onChange={(e) => setClient({ legalUrls: { ...legal, privacy: e.target.value } })} /></div>
          <div><label className={labelClass}>URL cookies</label>
            <input className={inputClass} disabled={readOnly} placeholder="https://" value={legal.cookies || ''} onChange={(e) => setClient({ legalUrls: { ...legal, cookies: e.target.value } })} /></div>
          <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
            <input type="checkbox" disabled={readOnly} checked={!!client.vidaTermsAccepted} onChange={(e) => setClient({ vidaTermsAccepted: e.target.checked })} />
            Acepta los textos de VIDA
            {client.vidaTermsAcceptedAt && <span className="text-xs text-gray-400">({new Date(client.vidaTermsAcceptedAt).toLocaleDateString('es-ES')})</span>}
          </label>
        </div>

        <h3 className="text-sm font-semibold text-gray-900 mt-5">Valoración interna</h3>
        <p className="text-xs text-gray-400 mb-2">Solo nuestro equipo. Nunca se envía al lead.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={labelClass}>Apreciaciones</label>{ta(internal.appreciations, (v) => setInternal({ appreciations: v }), 3)}</div>
          <div><label className={labelClass}>Aspectos críticos</label>{ta(internal.criticalPoints, (v) => setInternal({ criticalPoints: v }), 3)}</div>
          <div><label className={labelClass}>Procesos clave</label>{ta(internal.keyProcesses, (v) => setInternal({ keyProcesses: v }), 3)}</div>
          <div><label className={labelClass}>Hoja de ruta</label>{ta(internal.roadmap, (v) => setInternal({ roadmap: v }), 3)}</div>
          {ABC.map((g) => (
            <div key={g.key} className="sm:col-span-2"><label className={labelClass}>Soluciones {g.label}</label>
              <SolutionsInput disabled={readOnly} value={solutions[g.key] || []}
                onChange={(v) => setInternal({ solutions: { ...solutions, [g.key]: v } })} /></div>
          ))}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">{readOnly ? 'Cerrar' : 'Cancelar'}</button>
          {!readOnly && (
            <button onClick={submit} disabled={busy} className="px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">
              {busy ? 'Guardando…' : 'Guardar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
